import { CharacterData, EquipmentItem, EquipmentReference, ClassFeature, Modifier, ContentReference } from './character';
import { ExperienceEntry, MarkedTraits, TierAdvancementState, LevelUpChanges, Level } from './levelUp';
import { LootReference, ConsumableReference } from './equipment';

export interface PendingOperation {
  id: string;
  type: 'addEquipment' | 'removeEquipment' | 'equipItem' | 'unequipItem' |
        'addEquipmentReference' | 'removeEquipmentReference' | 'equipItemReference' | 'unequipItemReference' |
        'addLootReference' | 'removeLootReference' | 'updateLootReferenceQuantity' |
        'addConsumableReference' | 'removeConsumableReference' | 'updateConsumableReferenceQuantity' |
        'addDomainCardToLoadout' | 'moveDomainCardToVault' | 'moveDomainCardToLoadout' | 'removeDomainCardFromVaultLoadout' |
        'addClassFeature' | 'removeClassFeature' | 'activateClassFeature' | 'deactivateClassFeature' |
        'addModifier' | 'removeModifier' |
        'updateCharacterInfo' | 'updateBaseStats' | 'updateCurrentState' | 'updateManualOverrides' |
        'updateExperiences' | 'updateInventoryManualItems' | 'updateInventoryDomainCards' | 'updateBiography' |
        'updateMarkedTraits' | 'updateTierAdvancements' | 'updateLevelUpHistory' | 'updateMulticlass';
  operation: (char: CharacterData) => CharacterData;
  timestamp: number;
  retryCount: number;
}

export interface UpdateState {
  // Single source of truth
  baseCharacter: CharacterData | null;
  
  // Operation queue for updates
  pendingOperations: PendingOperation[];
  
  // Persistence state
  lastSavedVersion: number;
  saving: boolean;
  error: string | null;
  
  // Loading states
  loading: boolean;
  charactersLoading: boolean;
  
  // User characters list
  characters: CharacterData[];
}

export type UpdateAction = 
  | { type: 'SET_BASE_CHARACTER'; character: CharacterData | null }
  | { type: 'QUEUE_OPERATION'; operation: PendingOperation }
  | { type: 'OPERATIONS_SAVED'; clearedOperationIds: string[] }
  | { type: 'SAVE_FAILED'; error: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_CHARACTERS_LOADING'; loading: boolean }
  | { type: 'SET_CHARACTERS'; characters: CharacterData[] }
  | { type: 'SET_SAVING'; saving: boolean }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RETRY_OPERATION'; operationId: string }
  | { type: 'REMOVE_OPERATION'; operationId: string };

export const createInitialUpdateState = (): UpdateState => ({
  baseCharacter: null,
  pendingOperations: [],
  lastSavedVersion: 0,
  saving: false,
  error: null,
  loading: false,
  charactersLoading: false,
  characters: [],
});

// Operation factory functions for type safety

export const createAddEquipmentOperation = (equipment: EquipmentItem): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addEquipment',
  operation: (char: CharacterData) => ({
    ...char,
    equipment: [...char.equipment, equipment]
  })
});

export const createRemoveEquipmentOperation = (equipmentId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeEquipment',
  operation: (char: CharacterData) => ({
    ...char,
    equipment: char.equipment.filter(e => e.id !== equipmentId)
  })
});

export const createEquipItemOperation = (equipmentId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'equipItem',
  operation: (char: CharacterData) => ({
    ...char,
    equipment: char.equipment.map(item =>
      item.id === equipmentId ? { ...item, equipped: true } : item
    )
  })
});

export const createUnequipItemOperation = (equipmentId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'unequipItem',
  operation: (char: CharacterData) => ({
    ...char,
    equipment: char.equipment.map(item =>
      item.id === equipmentId ? { ...item, equipped: false } : item
    )
  })
});


export const createAddClassFeatureOperation = (feature: ClassFeature): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addClassFeature',
  operation: (char: CharacterData) => ({
    ...char,
    classFeatures: [...char.classFeatures, feature]
  })
});

export const createRemoveClassFeatureOperation = (featureId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeClassFeature',
  operation: (char: CharacterData) => ({
    ...char,
    classFeatures: char.classFeatures.filter(f => f.id !== featureId)
  })
});

export const createActivateClassFeatureOperation = (featureId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'activateClassFeature',
  operation: (char: CharacterData) => ({
    ...char,
    classFeatures: char.classFeatures.map(feature =>
      feature.id === featureId ? { ...feature, active: true } : feature
    )
  })
});

export const createDeactivateClassFeatureOperation = (featureId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'deactivateClassFeature',
  operation: (char: CharacterData) => ({
    ...char,
    classFeatures: char.classFeatures.map(feature =>
      feature.id === featureId ? { ...feature, active: false } : feature
    )
  })
});


export const createAddModifierOperation = (modifier: Modifier): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addModifier',
  operation: (char: CharacterData) => ({
    ...char,
    activeModifiers: [...char.activeModifiers, modifier]
  })
});

export const createRemoveModifierOperation = (modifierId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeModifier',
  operation: (char: CharacterData) => ({
    ...char,
    activeModifiers: char.activeModifiers.filter(m => m.id !== modifierId)
  })
});

export const createUpdateCharacterInfoOperation = (updates: Partial<Pick<CharacterData, 'name' | 'class' | 'heritage' | 'level' | 'tier' | 'subclass' | 'subclassCardTier'>>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateCharacterInfo',
  operation: (char: CharacterData) => ({
    ...char,
    ...updates
  })
});

export const createUpdateBaseStatsOperation = (updates: Partial<CharacterData['baseStats']>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateBaseStats',
  operation: (char: CharacterData) => ({
    ...char,
    baseStats: {
      ...char.baseStats,
      ...updates,
      // Handle nested trait updates
      traits: updates.traits ? {
        ...char.baseStats.traits,
        ...updates.traits
      } : char.baseStats.traits,
      // Handle nested threshold updates
      thresholds: updates.thresholds ? {
        ...char.baseStats.thresholds,
        ...updates.thresholds
      } : char.baseStats.thresholds
    }
  })
});

export const createUpdateCurrentStateOperation = (updates: Partial<CharacterData['currentState']>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateCurrentState',
  operation: (char: CharacterData) => ({
    ...char,
    currentState: {
      ...char.currentState,
      ...updates
    }
  })
});

export const createUpdateManualOverridesOperation = (updates: Partial<NonNullable<CharacterData['manualOverrides']>>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateManualOverrides',
  operation: (char: CharacterData) => {
    // If all updates are undefined or null, remove manualOverrides entirely
    const hasAnyOverride = Object.entries(updates).some(([, value]) =>
      value !== undefined && value !== null
    );

    if (!hasAnyOverride && !char.manualOverrides) {
      return char; // No changes needed
    }

    const currentOverrides = char.manualOverrides || {};

    // Build new overrides, explicitly handling undefined as deletion
    const newOverrides: any = {};

    // Process each field from updates
    for (const [key, value] of Object.entries(updates)) {
      if (value !== undefined) {
        // Only include fields that are not undefined
        newOverrides[key] = value;
      }
    }

    // Add any existing overrides that weren't mentioned in updates
    for (const [key, value] of Object.entries(currentOverrides)) {
      if (!(key in updates)) {
        newOverrides[key] = value;
      }
    }

    // If no overrides remain, remove the field entirely
    if (Object.keys(newOverrides).length === 0) {
      const { manualOverrides, ...rest } = char;
      return rest as CharacterData;
    }

    return {
      ...char,
      manualOverrides: newOverrides
    };
  }
});

// NEW: Reference-based equipment operations
export const createAddEquipmentReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addEquipmentReference',
  operation: (char: CharacterData) => {
    const existingRefIndex = char.inventory.equipmentReferences.findIndex(ref => ref.itemId === itemId);
    
    if (existingRefIndex >= 0) {
      // Item already exists, increment quantity
      const updatedReferences = [...char.inventory.equipmentReferences];
      updatedReferences[existingRefIndex] = {
        ...updatedReferences[existingRefIndex],
        quantity: updatedReferences[existingRefIndex].quantity + quantity
      };
      
      return {
        ...char,
        inventory: {
          ...char.inventory,
          equipmentReferences: updatedReferences
        }
      };
    } else {
      // New item, add reference
      const newReference: EquipmentReference = {
        itemId,
        quantity,
        equippedCount: 0
      };
      
      return {
        ...char,
        inventory: {
          ...char.inventory,
          equipmentReferences: [...char.inventory.equipmentReferences, newReference]
        }
      };
    }
  }
});

export const createRemoveEquipmentReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeEquipmentReference',
  operation: (char: CharacterData) => {
    const existingRefIndex = char.inventory.equipmentReferences.findIndex(ref => ref.itemId === itemId);
    
    if (existingRefIndex < 0) {
      // Item doesn't exist, no change
      return char;
    }
    
    const existingRef = char.inventory.equipmentReferences[existingRefIndex];
    const newQuantity = existingRef.quantity - quantity;
    
    if (newQuantity <= 0) {
      // Remove item entirely
      return {
        ...char,
        inventory: {
          ...char.inventory,
          equipmentReferences: char.inventory.equipmentReferences.filter(ref => ref.itemId !== itemId)
        }
      };
    } else {
      // Reduce quantity, ensure equipped count doesn't exceed new quantity
      const updatedReferences = [...char.inventory.equipmentReferences];
      updatedReferences[existingRefIndex] = {
        ...existingRef,
        quantity: newQuantity,
        equippedCount: Math.min(existingRef.equippedCount, newQuantity)
      };
      
      return {
        ...char,
        inventory: {
          ...char.inventory,
          equipmentReferences: updatedReferences
        }
      };
    }
  }
});

export const createEquipItemReferenceOperation = (itemId: string, count: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'equipItemReference',
  operation: (char: CharacterData) => {
    const existingRefIndex = char.inventory.equipmentReferences.findIndex(ref => ref.itemId === itemId);
    
    if (existingRefIndex < 0) {
      // Item doesn't exist, no change
      return char;
    }
    
    const existingRef = char.inventory.equipmentReferences[existingRefIndex];
    const newEquippedCount = Math.min(existingRef.equippedCount + count, existingRef.quantity);
    
    const updatedReferences = [...char.inventory.equipmentReferences];
    updatedReferences[existingRefIndex] = {
      ...existingRef,
      equippedCount: newEquippedCount
    };
    
    return {
      ...char,
      inventory: {
        ...char.inventory,
        equipmentReferences: updatedReferences
      }
    };
  }
});

export const createUnequipItemReferenceOperation = (itemId: string, count: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'unequipItemReference',
  operation: (char: CharacterData) => {
    const existingRefIndex = char.inventory.equipmentReferences.findIndex(ref => ref.itemId === itemId);
    
    if (existingRefIndex < 0) {
      // Item doesn't exist, no change
      return char;
    }
    
    const existingRef = char.inventory.equipmentReferences[existingRefIndex];
    const newEquippedCount = Math.max(existingRef.equippedCount - count, 0);
    
    const updatedReferences = [...char.inventory.equipmentReferences];
    updatedReferences[existingRefIndex] = {
      ...existingRef,
      equippedCount: newEquippedCount
    };
    
    return {
      ...char,
      inventory: {
        ...char.inventory,
        equipmentReferences: updatedReferences
      }
    };
  }
});

export const createUpdateExperiencesOperation = (experiences: ExperienceEntry[]): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateExperiences',
  operation: (char: CharacterData) => {
    return {
      ...char,
      experiences: [...experiences]
    };
  }
});

export const createUpdateInventoryManualItemsOperation = (manualItems: Array<{ name: string; description: string; quantity: number }>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateInventoryManualItems',
  operation: (char: CharacterData) => {
    return {
      ...char,
      inventory: {
        ...char.inventory,
        manualItems: [...manualItems]
      }
    };
  }
});

export const createUpdateInventoryDomainCardsOperation = (domainCardLoadout: ContentReference[], domainCardVault: ContentReference[]): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateInventoryDomainCards',
  operation: (char: CharacterData) => {
    return {
      ...char,
      inventory: {
        ...char.inventory,
        domainCardLoadout: [...domainCardLoadout],
        domainCardVault: [...domainCardVault]
      }
    };
  }
});

// Vault & Loadout System Operations (Phase 48) - NO LEGACY SUPPORT

export const createAddDomainCardToLoadoutOperation = (cardId: string, autoVaultIfFull: boolean = true): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addDomainCardToLoadout',
  operation: (char: CharacterData) => {
    const loadout = char.inventory.domainCardLoadout || [];
    const vault = char.inventory.domainCardVault || [];
    
    // Check if card already exists in loadout or vault
    const existsInLoadout = loadout.some(ref => ref.contentId === cardId);
    const existsInVault = vault.some(ref => ref.contentId === cardId);
    
    if (existsInLoadout || existsInVault) {
      return char; // Card already exists, no change
    }

    const newReference: ContentReference = {
      contentId: cardId,
      contentType: 'domain_card',
      quantity: 1
    };

    let newLoadout = [...loadout];
    let newVault = [...vault];

    // If loadout is full (5 cards) and auto-vault is enabled
    if (newLoadout.length >= 5 && autoVaultIfFull) {
      // Move oldest card to vault to make space
      const movedCard = newLoadout.shift()!;
      newVault = [...newVault, movedCard];
    }

    // Add new card to loadout (if there's space or we made space)
    if (newLoadout.length < 5) {
      newLoadout = [...newLoadout, newReference];
    } else {
      // Loadout is full and no auto-vault, add to vault instead
      newVault = [...newVault, newReference];
    }

    return {
      ...char,
      inventory: {
        ...char.inventory,
        domainCardLoadout: newLoadout,
        domainCardVault: newVault
      }
    };
  }
});

export const createMoveDomainCardToVaultOperation = (cardId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'moveDomainCardToVault',
  operation: (char: CharacterData) => {
    const loadout = char.inventory.domainCardLoadout || [];
    const vault = char.inventory.domainCardVault || [];
    
    const cardIndex = loadout.findIndex(ref => ref.contentId === cardId);
    if (cardIndex === -1) {
      return char; // Card not in loadout
    }

    const cardToMove = loadout[cardIndex];
    const newLoadout = loadout.filter((_, index) => index !== cardIndex);
    const newVault = [...vault, cardToMove];

    return {
      ...char,
      inventory: {
        ...char.inventory,
        domainCardLoadout: newLoadout,
        domainCardVault: newVault
      }
    };
  }
});

export const createMoveDomainCardToLoadoutOperation = (cardId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'moveDomainCardToLoadout',
  operation: (char: CharacterData) => {
    const loadout = char.inventory.domainCardLoadout || [];
    const vault = char.inventory.domainCardVault || [];
    
    const cardIndex = vault.findIndex(ref => ref.contentId === cardId);
    if (cardIndex === -1) {
      return char; // Card not in vault
    }

    // Check loadout capacity
    if (loadout.length >= 5) {
      return char; // Loadout is full - caller should handle space management
    }

    const cardToMove = vault[cardIndex];
    const newVault = vault.filter((_, index) => index !== cardIndex);
    const newLoadout = [...loadout, cardToMove];

    return {
      ...char,
      inventory: {
        ...char.inventory,
        domainCardLoadout: newLoadout,
        domainCardVault: newVault
      }
    };
  }
});

export const createRemoveDomainCardFromVaultLoadoutOperation = (cardId: string): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeDomainCardFromVaultLoadout',
  operation: (char: CharacterData) => {
    const loadout = char.inventory.domainCardLoadout || [];
    const vault = char.inventory.domainCardVault || [];
    
    const newLoadout = loadout.filter(ref => ref.contentId !== cardId);
    const newVault = vault.filter(ref => ref.contentId !== cardId);

    return {
      ...char,
      inventory: {
        ...char.inventory,
        domainCardLoadout: newLoadout,
        domainCardVault: newVault
      }
    };
  }
});

export const createUpdateBiographyOperation = (updates: Partial<CharacterData['biography']>): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateBiography',
  operation: (char: CharacterData) => ({
    ...char,
    biography: {
      ...char.biography,
      ...updates
    }
  })
});

// Level-Up System Operations (Phase 26A)

export const createUpdateMarkedTraitsOperation = (markedTraits: MarkedTraits): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateMarkedTraits',
  operation: (char: CharacterData) => ({
    ...char,
    markedTraits: { ...markedTraits }
  })
});

export const createUpdateTierAdvancementsOperation = (tierAdvancements: CharacterData['tierAdvancements']): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateTierAdvancements',
  operation: (char: CharacterData) => ({
    ...char,
    tierAdvancements: tierAdvancements ? { ...tierAdvancements } : undefined
  })
});

export const createUpdateLevelUpHistoryOperation = (levelUpHistory: CharacterData['levelUpHistory']): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateLevelUpHistory',
  operation: (char: CharacterData) => ({
    ...char,
    levelUpHistory: levelUpHistory ? [...levelUpHistory] : undefined
  })
});

// Multiclass System Operations (Phase 26B)

export const createUpdateMulticlassOperation = (multiclass: CharacterData['multiclass']): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateMulticlass',
  operation: (char: CharacterData) => ({
    ...char,
    multiclass: multiclass ? { ...multiclass } : undefined
  })
});

// ============================================================================
// Loot & Consumable Operations (Phase 2)
// ============================================================================

/**
 * Add a loot item reference to inventory
 * If item already exists, increments quantity
 */
export const createAddLootReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addLootReference',
  operation: (char: CharacterData) => {
    const currentLootRefs = char.inventory.lootReferences || [];
    const existingRefIndex = currentLootRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex >= 0) {
      // Item exists, increment quantity
      const updatedRefs = [...currentLootRefs];
      updatedRefs[existingRefIndex] = {
        ...updatedRefs[existingRefIndex],
        quantity: (updatedRefs[existingRefIndex].quantity || 1) + quantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: updatedRefs
        }
      };
    } else {
      // New item, add to array
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: [...currentLootRefs, { itemId, quantity }]
        }
      };
    }
  }
});

/**
 * Remove a loot item reference from inventory
 * If quantity would go to 0 or below, removes the reference entirely
 */
export const createRemoveLootReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeLootReference',
  operation: (char: CharacterData) => {
    const currentLootRefs = char.inventory.lootReferences || [];
    const existingRefIndex = currentLootRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex < 0) {
      return char; // Item not found, no change
    }

    const existingRef = currentLootRefs[existingRefIndex];
    const newQuantity = (existingRef.quantity || 1) - quantity;

    if (newQuantity <= 0) {
      // Remove the reference entirely
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: currentLootRefs.filter(ref => ref.itemId !== itemId)
        }
      };
    } else {
      // Decrement quantity
      const updatedRefs = [...currentLootRefs];
      updatedRefs[existingRefIndex] = {
        ...existingRef,
        quantity: newQuantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: updatedRefs
        }
      };
    }
  }
});

/**
 * Add a consumable item reference to inventory
 * If item already exists, increments quantity (max 5 per subtype)
 */
export const createAddConsumableReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'addConsumableReference',
  operation: (char: CharacterData) => {
    const currentConsumableRefs = char.inventory.consumableReferences || [];
    const existingRefIndex = currentConsumableRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex >= 0) {
      // Item exists, increment quantity (note: max-5 validation should be done before calling)
      const updatedRefs = [...currentConsumableRefs];
      updatedRefs[existingRefIndex] = {
        ...updatedRefs[existingRefIndex],
        quantity: updatedRefs[existingRefIndex].quantity + quantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: updatedRefs
        }
      };
    } else {
      // New item, add to array
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: [...currentConsumableRefs, { itemId, quantity }]
        }
      };
    }
  }
});

/**
 * Remove a consumable item reference from inventory
 * If quantity would go to 0 or below, removes the reference entirely
 */
export const createRemoveConsumableReferenceOperation = (itemId: string, quantity: number = 1): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'removeConsumableReference',
  operation: (char: CharacterData) => {
    const currentConsumableRefs = char.inventory.consumableReferences || [];
    const existingRefIndex = currentConsumableRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex < 0) {
      return char; // Item not found, no change
    }

    const existingRef = currentConsumableRefs[existingRefIndex];
    const newQuantity = existingRef.quantity - quantity;

    if (newQuantity <= 0) {
      // Remove the reference entirely
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: currentConsumableRefs.filter(ref => ref.itemId !== itemId)
        }
      };
    } else {
      // Decrement quantity
      const updatedRefs = [...currentConsumableRefs];
      updatedRefs[existingRefIndex] = {
        ...existingRef,
        quantity: newQuantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: updatedRefs
        }
      };
    }
  }
});

/**
 * Update quantity for a specific loot item
 */
export const createUpdateLootReferenceQuantityOperation = (itemId: string, newQuantity: number): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateLootReferenceQuantity',
  operation: (char: CharacterData) => {
    const currentLootRefs = char.inventory.lootReferences || [];
    const existingRefIndex = currentLootRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex < 0) {
      return char; // Item not found, no change
    }

    if (newQuantity <= 0) {
      // Remove the reference
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: currentLootRefs.filter(ref => ref.itemId !== itemId)
        }
      };
    } else {
      // Update quantity
      const updatedRefs = [...currentLootRefs];
      updatedRefs[existingRefIndex] = {
        ...updatedRefs[existingRefIndex],
        quantity: newQuantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          lootReferences: updatedRefs
        }
      };
    }
  }
});

/**
 * Update quantity for a specific consumable item
 */
export const createUpdateConsumableReferenceQuantityOperation = (itemId: string, newQuantity: number): Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'> => ({
  type: 'updateConsumableReferenceQuantity',
  operation: (char: CharacterData) => {
    const currentConsumableRefs = char.inventory.consumableReferences || [];
    const existingRefIndex = currentConsumableRefs.findIndex(ref => ref.itemId === itemId);

    if (existingRefIndex < 0) {
      return char; // Item not found, no change
    }

    if (newQuantity <= 0) {
      // Remove the reference
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: currentConsumableRefs.filter(ref => ref.itemId !== itemId)
        }
      };
    } else {
      // Update quantity
      const updatedRefs = [...currentConsumableRefs];
      updatedRefs[existingRefIndex] = {
        ...updatedRefs[existingRefIndex],
        quantity: newQuantity
      };
      return {
        ...char,
        inventory: {
          ...char.inventory,
          consumableReferences: updatedRefs
        }
      };
    }
  }
});

